import { AxiosError, AxiosResponse } from 'axios'
import useAnalytics, { AnalyticsCurrency, AnalyticsEvents } from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { ImageFile } from 'common/types/image'
import { formatDate } from 'common/utils/date'
import { numberSchema } from 'common/utils/yup'
import { parseISO } from 'date-fns'
import { useSpeciesName } from 'modules/pond/api/selector'
import { SpeciesName } from 'modules/pond/constants'
import {
  useMutateCreateShrimpSales,
  useMutateUpdateShrimpSales,
  useMutateUploadBill,
} from 'modules/shrimpSales/api/mutation'
import {
  useGetLatestShrimpSalesCategory,
  useShrimpSalesSummary,
} from 'modules/shrimpSales/api/queries'
import { ShrimpSales, ShrimpSalesCategory } from 'modules/shrimpSales/api/types'
import { FISH_CATEGORIES, SHRIMP_CATEGORIES } from 'modules/shrimpSales/constants'
import { shrimpSalesDateFilterState } from 'modules/shrimpSales/stores/atom'
import { ShrimpCategoryRecord } from 'modules/shrimpSales/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { normalizeSpeciesName } from 'utils/analytics'
import * as Yup from 'yup'

// DOF-1184: sums quantity*price across every category/subcategory the form
// holds. The server-computed `totalRevenue` isn't available synchronously
// from the create/update mutation response (it's only populated by a later
// list refetch), so this mirrors the calculation client-side for the
// `sale_value` param.
export const computeSaleValue = (categories: ShrimpCategoryRecord[]): number =>
  categories.reduce(
    (total, category) =>
      total +
      category.subCategories.reduce(
        (subtotal, sub) => subtotal + (Number(sub.amount) || 0) * (Number(sub.price) || 0),
        0
      ),
    0
  )

import { Box } from '@mui/material'

import DialogForm from './DialogForm'
import SummaryForm from './SummaryForm'

export interface ShrimpSalesFormDataType {
  date: Date
  buyer: string
  shrimpTypeId: number
  shrimpPerKg: string
  categories: ShrimpCategoryRecord[]
  image: ImageFile | string | null
  note: string
}

interface ShrimpSalesFormProps {
  data?: ShrimpSales
  onDelete?: (id: number) => void
  onSubmitForm?: () => void
  onSubmitFinally?: () => void
  onSubmitSuccess?: () => void
  onSubmitFailed?: () => void
  onCancel?: () => void
  isDialog?: boolean
  step?: 1 | 2
  onChangeStep?: (step: 1 | 2) => void
}

const ShrimpSalesForm = ({
  data,
  onSubmitForm,
  onSubmitFinally,
  onSubmitSuccess,
  onCancel,
  isDialog,
  onSubmitFailed,
  step = 1,
  onChangeStep,
}: ShrimpSalesFormProps) => {
  const { t } = useTranslation('shrimpSales')
  const { refetch } = useShrimpSalesSummary()
  const { data: latestCategory } = useGetLatestShrimpSalesCategory(!data)
  const [editMode, setEditMode] = useState(false)

  const date = useRecoilValue(shrimpSalesDateFilterState)

  const recordShrimpSalesMutation = useMutateCreateShrimpSales()
  const uploadBillMutation = useMutateUploadBill()
  const updateShrimpSalesMutation = useMutateUpdateShrimpSales()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const amountSchema = numberSchema(t('require.number'))

  const schema = Yup.object().shape({
    date: Yup.date(),
    buyer: Yup.string().required(t('require.buyer')),
    shrimpTypeId: Yup.number().positive(t('require.shrimp.type')),
    shrimpPerKg: amountSchema.required(t('require.number')),
    categories: Yup.array().of(
      Yup.object().shape({
        id: Yup.number(),
        name: Yup.string(),
        subCategories: Yup.array().of(
          Yup.object().shape({
            id: Yup.number(),
            name: Yup.string(),
            amount: isDialog ? amountSchema : amountSchema.required(t('require.number')),
            price: isDialog ? amountSchema : amountSchema.required(t('require.number')),
          })
        ),
      })
    ),
    image: Yup.mixed().nullable(),
    note: Yup.string().nullable(),
  })

  const resolver = useYupValidationResolver(schema)

  const defaultValues = {
    date: new Date(),
    buyer: '',
    shrimpTypeId: -1,
    shrimpPerKg: '',
    categories: speciesName === SpeciesName.Fish ? FISH_CATEGORIES : SHRIMP_CATEGORIES,
    image: null,
    note: '',
  }

  const hookForm = useForm<ShrimpSalesFormDataType>({
    defaultValues,
    resolver,
  })

  const {
    reset,
    setValue,
    setError,
    formState: { isDirty },
  } = hookForm
  const latestPrefilledRef = useRef(false)

  const turnOffEditMode = () => {
    setEditMode(false)
    resetToOriginal()
  }

  const preparedCategories = (
    data: ShrimpSalesCategory[],
    withValues = false
  ): ShrimpCategoryRecord[] =>
    (data ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      subCategories: (c.subcategories ?? []).map((sc) => ({
        id: sc.id,
        name: sc.name,
        amount: withValues ? `${sc.quantityInKg}` : '',
        price: withValues ? `${sc.pricePerKg}` : '',
      })),
    }))

  const resetToOriginal = useCallback(() => {
    if (data) {
      reset((values) => ({
        ...values,
        date: parseISO(data.date),
        buyer: data.buyerName,
        shrimpTypeId: data.shrimpSalesTypeId,
        shrimpPerKg: `${data.shrimpCountPerKg}`,
        image: data.billPhotoURL,
        note: data.note ?? '',
        categories: preparedCategories(data.categories, true),
      }))
    }
  }, [data, reset, speciesName])

  useEffect(() => {
    resetToOriginal()
  }, [resetToOriginal])

  useEffect(() => {
    if (data || latestPrefilledRef.current || isDirty) return
    if (!latestCategory?.categories?.length) return
    latestPrefilledRef.current = true
    reset((values) => ({
      ...values,
      categories: preparedCategories(latestCategory.categories),
    }))
  }, [data, latestCategory, reset, isDirty])

  useEffect(() => {
    // Editing keeps the record's own date; the filter date only seeds new records.
    if (date && !data) {
      reset((values) => ({
        ...values,
        date: date,
      }))
    }
  }, [date])

  const onSuccess = (res: AxiosResponse, isUpdate: boolean, saleValue?: number) => {
    const { status } = res.data
    if (status === 200) {
      reset()
      onSubmitSuccess?.()
      setTimeout(() => {
        refetch()
      }, 1000)
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      if (isUpdate) {
        trackEvent(
          AnalyticsEvents.salesRecordUpdated,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      } else {
        trackEvent(AnalyticsEvents.salesRecordCreated, {
          ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
          sale_value: saleValue ?? 0,
          sale_currency: AnalyticsCurrency.thb,
        })
      }
    }
  }

  const onError = (error: unknown, _variables: any, _context: unknown) => {
    const { response } = error as AxiosError
    if (response?.status === 400) {
      setError('date', {
        type: 'server',
        message: t('shrimpSales:duplicate.day'),
      })
    }
    onSubmitFailed?.()
  }

  const handleDrop = (acceptedFiles: ImageFile[]) => {
    const file = acceptedFiles[0]
    if (file) {
      const photoFile = file
      photoFile.preview = URL.createObjectURL(file)
      setValue('image', file)
    }
  }

  const onSubmit = async (value: ShrimpSalesFormDataType) => {
    onSubmitForm?.()
    const date = formatDate(value.date)
    const shrimpPerKgNumber = Number(value.shrimpPerKg)
    const buyer = value.buyer
    const note = value.note
    let imgUrl = ''
    const bill = value.image

    if (bill && typeof bill !== 'string') {
      const formData = new FormData()
      formData.append('image', bill)
      const { data } = await uploadBillMutation.mutateAsync(formData)
      imgUrl = data.data
    } else {
      imgUrl = value.image as string
    }

    if (isDialog) {
      if (data) {
        const body = {
          date,
          note,
          buyerName: buyer,
          shrimpSalesTypeId: value.shrimpTypeId,
          shrimpCountPerKg: shrimpPerKgNumber,
          billPhotoURL: imgUrl,
          categories: value.categories.map((category) => ({
            id: category.id === -1 ? null : category.id,
            shrimpSalesId: data.id,
            name: category.name,
            subcategories: category.subCategories.map((sub) => ({
              id: sub.id,
              name: sub.name,
              quantityInKg: Number(sub.amount),
              pricePerKg: Number(sub.price),
              shrimpSalesCategoryId: category.id,
            })),
          })),
        }

        await updateShrimpSalesMutation
          .mutateAsync(
            {
              salesId: data.id,
              body,
            },
            { onSuccess: (res) => onSuccess(res, true), onError }
          )
          .finally(() => onSubmitFinally?.())
        return
      }
      const body = {
        date,
        note,
        buyerName: buyer,
        shrimpSalesTypeId: value.shrimpTypeId,
        shrimpCountPerKg: shrimpPerKgNumber,
        billPhotoURL: imgUrl,
        categories: value.categories.map((category) => ({
          name: category.name,
          subcategories: category.subCategories.map((sub) => ({
            name: sub.name,
            quantityInKg: Number(sub.amount),
            pricePerKg: Number(sub.price),
          })),
        })),
      }
      await recordShrimpSalesMutation
        .mutateAsync(
          {
            body,
          },
          { onSuccess: (res) => onSuccess(res, false, computeSaleValue(value.categories)), onError }
        )
        .finally(() => onSubmitFinally?.())
      return
    }

    if (data) {
      const body = {
        date,
        note,
        buyerName: buyer,
        shrimpSalesTypeId: value.shrimpTypeId,
        shrimpCountPerKg: shrimpPerKgNumber,
        billPhotoURL: imgUrl,
        categories: value.categories.map((category) => ({
          id: category.id === -1 ? null : category.id,
          shrimpSalesId: data.id,
          name: category.name,
          subcategories: category.subCategories.map((sub) => ({
            id: sub.id,
            name: sub.name,
            quantityInKg: Number(sub.amount),
            pricePerKg: Number(sub.price),
            shrimpSalesCategoryId: category.id,
          })),
        })),
      }
      await updateShrimpSalesMutation
        .mutateAsync(
          {
            salesId: data.id,
            body,
          },
          {
            onSuccess: () => {
              turnOffEditMode()
              refetch()
              const normalizedSpecies = normalizeSpeciesName(speciesName)
              trackEvent(
                AnalyticsEvents.salesRecordUpdated,
                normalizedSpecies ? { species: normalizedSpecies } : undefined
              )
            },
            onError,
          }
        )
        .finally(() => onSubmitFinally?.())
      return
    }
  }

  return (
    <FormProvider {...hookForm}>
      {isDialog ? (
        <Box
          p={1}
          sx={{
            ml: step == 1 ? -3 : 0,
          }}
        >
          <DialogForm
            step={step}
            onSubmit={onSubmit}
            onDropPhoto={handleDrop}
            onCancel={onCancel}
            onChangeStep={onChangeStep}
          />
        </Box>
      ) : (
        <Box p={0}>
          {data ? (
            <SummaryForm
              data={data}
              isEdit={editMode}
              onSubmit={onSubmit}
              onDropPhoto={handleDrop}
            />
          ) : null}
        </Box>
      )}
    </FormProvider>
  )
}

export default ShrimpSalesForm
